
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import PageTransition from "@/components/PageTransition";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase, getUserIdForRecommendation } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import LoginRequired from "@/components/LoginRequired";

// Define the WishlistItem type to match the structure
interface WishlistItem {
  id: string;
  item_name: string;
  vendor: string;
  price: number;
  metadata?: {
    description?: string;
    [key: string]: any;
  };
  [key: string]: any;
}

const Wishlist = () => {
  // Auth state
  const [isLoadingInitial, setIsLoadingInitial] = useState(true);
  const [currentAuthUserId, setCurrentAuthUserId] = useState<string | null>(null);
  const [mappedUserId, setMappedUserId] = useState<string | null>(null);

  // Fetch current session on component mount
  useEffect(() => {
    const fetchSession = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        console.log("Initial session check:", data.session);
        
        if (data.session?.user?.id) {
          const authUserId = data.session.user.id;
          setCurrentAuthUserId(authUserId);
          
          // Try to map the auth ID to a user ID for recommendations
          const userIdForWishlist = await getUserIdForRecommendation(authUserId);
          
          if (userIdForWishlist) {
            console.log("Using user ID for wishlist:", userIdForWishlist);
            setMappedUserId(userIdForWishlist);
          } else {
            console.warn("Could not map user ID for wishlist");
          }
        }
      } catch (err) {
        console.error("Error fetching session:", err);
        toast.error("Failed to check login status");
      } finally {
        setIsLoadingInitial(false);
      }
    };

    fetchSession();

    // Set up auth state change listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log("Auth state changed:", event, session);
      const newAuthUserId = session?.user?.id || null;
      setCurrentAuthUserId(newAuthUserId);
      
      if (newAuthUserId) {
        // Try to map the auth ID to a user ID whenever auth state changes
        const newMappedUserId = await getUserIdForRecommendation(newAuthUserId);
        setMappedUserId(newMappedUserId);
      } else {
        setMappedUserId(null);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Query for wishlist items
  const { data: wishlistItems = [], isLoading: isLoadingWishlist, error } = useQuery({
    queryKey: ['wishlist', mappedUserId],
    queryFn: async () => {
      if (!mappedUserId) {
        return [];
      }
      
      console.log("Fetching wishlist for user:", mappedUserId);
      const { data, error } = await supabase.rpc('fetch_wishlist', {
        p_user_id: mappedUserId
      });
      
      if (error) {
        console.error("Error fetching wishlist:", error);
        toast.error("Failed to load wishlist items");
        throw error;
      }
      
      return Array.isArray(data) ? data as WishlistItem[] : [];
    },
    enabled: !!mappedUserId && !isLoadingInitial,
  });

  // Show loading state during initial authentication check
  if (isLoadingInitial) {
    return (
      <PageTransition>
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
        </div>
      </PageTransition>
    );
  }

  return (
    <PageTransition>
      <div className="space-y-8">
        <section className="text-center max-w-3xl mx-auto">
          <div className="inline-block px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
            Your Wishlist
          </div>
          <h1 className="text-4xl font-bold tracking-tight">
            Products You're Watching
          </h1>
          <p className="mt-4 text-lg text-muted-foreground">
            Track price changes and availability for items you're interested in.
          </p>
        </section>

        {isLoadingWishlist ? (
          <div className="flex items-center justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
          </div>
        ) : error ? (
          <Card>
            <CardHeader>
              <CardTitle>Error Loading Wishlist</CardTitle>
              <CardDescription>
                We encountered an error fetching your wishlist.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-destructive">Please try again later.</p>
            </CardContent>
          </Card>
        ) : !currentAuthUserId ? (
          <LoginRequired />
        ) : wishlistItems.length === 0 ? (
          <Card>
            <CardHeader>
              <CardTitle>Your Wishlist is Empty</CardTitle>
              <CardDescription>
                You haven't added any items to your wishlist yet.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-48 flex items-center justify-center bg-secondary/50 rounded-md">
                <p className="text-muted-foreground">
                  Browse recommendations and add items to start tracking them
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6">
            {wishlistItems.map((item: WishlistItem) => (
              <Card key={item.id}>
                <CardHeader>
                  <CardTitle>{item.item_name}</CardTitle>
                  <CardDescription>
                    {item.vendor}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="text-2xl font-bold text-primary">
                      ${item.price.toFixed(2)}
                    </div>
                    {item.metadata?.description && (
                      <p className="text-sm text-muted-foreground">
                        {item.metadata.description}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </PageTransition>
  );
};

export default Wishlist;
